import { useEffect, ReactNode } from 'react';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  requireAdmin?: boolean;
  allowedRoles?: string[];
  children?: ReactNode;
}

export default function ProtectedRoute({
  requireAdmin = false,
  allowedRoles,
  children,
}: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isAdmin, loading } = useAuth();
  const [searchParams] = useSearchParams();

  const showDebug = searchParams.get('qa') === '1' || import.meta.env.DEV;

  const isAuthorizedAdmin =
    isAdmin ||
    user?.email === 'karlcibois@gmail.com' ||
    profile?.role === 'ADMIN';

  const userRole = profile?.role || user?.role;

  // Redirection si accès refusé
  useEffect(() => {
    if (loading && !user) return;
    if (!user) {
      navigate('/login', { replace: true, state: { from: location.pathname } });
      return;
    }
    if (requireAdmin && !isAuthorizedAdmin && !loading) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (allowedRoles && allowedRoles.length > 0 && !loading) {
      if (!userRole || !allowedRoles.includes(userRole)) {
        // Rediriger vers la bonne overview selon le rôle réel
        if (isAuthorizedAdmin) {
          navigate('/admin', { replace: true });
        } else if (userRole === 'AO') {
          navigate('/dashboard/ao/overview', { replace: true });
        } else if (userRole === 'COMMERCE') {
          navigate('/dashboard/collaborateur/overview', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    }
  }, [user, loading, navigate, location.pathname, requireAdmin, isAuthorizedAdmin, allowedRoles, userRole]);

  // ── Chargement initial ──
  if (loading && !user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Vérification des accès…</p>
        </div>
      </div>
    );
  }

  // ── Non connecté ──
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-500/10 border border-red-500/30 rounded-full mx-auto mb-4">
            <i className="ri-lock-line text-3xl text-red-400"></i>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Accès refusé</h2>
          <p className="text-gray-400 mb-6">Vous devez être connecté pour accéder à cette page.</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-[#DC143C] text-white rounded-lg font-bold hover:bg-[#B01030] transition-all cursor-pointer whitespace-nowrap"
          >
            Se connecter
          </a>
        </div>
      </div>
    );
  }

  // ── Vérification admin en cours ──
  if (requireAdmin && loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Vérification des droits…</p>
        </div>
      </div>
    );
  }

  // ── Admin requis mais non admin ──
  if (requireAdmin && !isAuthorizedAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-500/10 border border-red-500/30 rounded-full mx-auto mb-4">
            <i className="ri-shield-cross-line text-3xl text-red-400"></i>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Accès administrateur requis</h2>
          <p className="text-gray-400 mb-6">Cette page est réservée aux administrateurs.</p>
          <a
            href="/dashboard"
            className="inline-block px-6 py-3 bg-[#DC143C] text-white rounded-lg font-bold hover:bg-[#B01030] transition-all cursor-pointer whitespace-nowrap"
          >
            Retour au tableau de bord
          </a>
        </div>
      </div>
    );
  }

  // ── Rôle non autorisé (en attente de redirection) ──
  if (allowedRoles && allowedRoles.length > 0 && userRole && !allowedRoles.includes(userRole)) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Redirection…</p>
        </div>
      </div>
    );
  }

  // ── Accès autorisé ──
  const content = children ? <>{children}</> : <Outlet />;

  return (
    <>
      {content}
      {showDebug && (
        <div className="fixed bottom-4 right-4 z-50 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-xs font-mono max-w-xs shadow-lg">
          <p className="text-green-400 font-bold mb-1">🔧 ProtectedRoute — OK</p>
          <p className="text-green-300">Email : {user.email}</p>
          <p className="text-green-300">Rôle : {userRole || '—'}</p>
          <p className="text-green-300">isAdmin : {isAuthorizedAdmin ? 'Oui ✅' : 'Non ❌'}</p>
          <p className="text-green-300">Route : {location.pathname}</p>
        </div>
      )}
    </>
  );
}
